// commit引数は使わないけど必要なものなので無視しておく
/* eslint unused-imports/no-unused-vars: ["warn", { vars: 'all', varsIgnorePattern: '^_', args: 'after-used', argsIgnorePattern: '^(_|commit)' }] */
/* eslint @typescript-eslint/no-unused-vars: ["warn", { vars: 'all', varsIgnorePattern: '^_', args: 'after-used', argsIgnorePattern: '^(_|commit)' }] */
import { AxiosResponse } from 'axios'
import { InjectionKey } from 'vue'
import { Commit, createStore, Store } from 'vuex'

import { ORDER_TYPES } from '@/common/const'
import { apiServer, apiServerFormData, setAuthHeader } from '@/helper/apiServerRequest'
import { isTokenExists, removeToken, setToken } from '@/helper/localStorageAccessor'
import { ICommonSuccessResponse, ISignInUserResponse } from '@/types/apiResponse'
import { AreaMaster } from '@/types/areaMaster'
import { RegisterBillingParams, RegisterBillingResponse } from '@/types/billings'
import {
  IsCreatedRoomResponse,
  GetRoomResponse,
  OpenRoomResponse,
  UpdateRoomResponse,
  CloseRoomResponse,
  JoinMemberResponse,
  LeaveMemberResponse,
  FetchMessagesResponse,
  SendMessageStatesResponse,
  SendMessageResponse,
  UpdateMessageResponse,
  DeleteMessageResponse,
  AddReactionResponse,
  RemoveReactionResponse,
} from '@/types/chat'
import { GenerateGmoLinkplusUrlParams, GenerateGmoLinkplusUrlResponse } from '@/types/gmo'
import {
  GetAgoraInfoResponse,
  GetAgoraInfoResponseData,
  GetAgoraAccessTokensParams,
  GetAgoraAccessTokensResponse,
  GetAgoraAccessTokensResponseData,
  HasLiveStreamingParams,
  HasLiveStreamingResponse,
  HasLiveStreamingResponseData,
  JoinLiveStreamByHostRequest,
  JoinLiveStreamByHostResponse,
  JoinLiveStreamByHostResponseData,
  LeaveLiveStreamByHostRequest,
  JoinLiveStreamByAudienceRequest,
  JoinLiveStreamByAudienceResponse,
  JoinLiveStreamByAudienceResponseData,
  LeaveLiveStreamByAudienceRequest,
  BlockAudienceRequest,
  ReleaseBlockedAudienceRequest,
  MuteAudienceRequest,
  ReleaseMutedAudienceRequest,
  GetLiveStreamsResponse,
  GetLiveStreamDetailResponse,
  GetBlockMapRequest,
  GetBlockMapResponse,
  GetBlockMapResponseData,
  GetMuteMapRequest,
  GetMuteMapResponse,
  GetMuteMapResponseData,
  GetAudiencesRequest,
  GetAudiencesResponse,
  GetAudiencesResponseData,
  GetProductsRequest,
  GetProductsResponse,
  GetProductsResponseData,
  HostedLiveStreamSummary,
  UpProductCountRequest,
  UpProductCountResponse,
  UpProductCountResponseData,
  DownProductCountRequest,
  DownProductCountResponse,
  DownProductCountResponseData,
  GenerateGmoLinkplusUrlForLiveStreamRequest,
  GenerateGmoLinkplusUrlForLiveStreamResponse,
  GenerateGmoLinkplusUrlForLiveStreamResponseData,
  GetLiveOrderRequest,
  GetLiveOrderResponse,
  GetLiveOrderResponseData,
} from '@/types/liveStream'
import { ListNgWordsResponseData } from '@/types/ngWord'
import { Notice } from '@/types/notices'
import { UnreadNotificationCount } from '@/types/notification'
import { OfferTermsList } from '@/types/offerTerms'
import {
  GetOrderDetailResponse,
  GetOrderedOrdersResponse,
  GetReceivedOrdersResponse,
  UpdateOrderResponse,
  RegisterOrderParams,
  RegisterOrderResponse,
  GetCompletedOrderSummaryParams,
  GetReceivedOrdersParams,
  CompletedOrderSummary,
  CancelOrderResponse,
  CancelOrderParams,
  UserReportRequest,
  UserReportResponse,
  GetCompletedOrdersParams,
  GetCompletedOrdersResponse,
} from '@/types/order'
import { IsRegisteredReviewResponse, RegisterReviewParams } from '@/types/review'
import {
  User,
  UserAuth,
  UserAuthMail,
  UserMailLogin,
  PasswordAuthMail,
  PasswordAuth,
  PasswordUpdate,
  SendAddMailAddressMailForm,
  VerifyAddMailAddress,
  AddMailAddressForm,
  VerifyAddMailAddressResponse,
  IsUserReportedResponse,
  phoneNumberAuthRequest,
  PhoneNumberAuthResponse,
  phoneNumberVerifyAuthRequest,
  PhoneNumberVerifyAuthResponse,
  QuitUserResponse,
  UserResponse,
  IsUserReceiverResponse,
} from '@/types/user'
import {
  ReceiveRegister,
  userBankRegisterBody,
  UserProfileUpdateRequest,
  LiveRegisterRequest,
} from '@/types/userDetail'
import {
  UserProfileDetailResponse,
  GetNewlyByJobNoStoreParams,
  GetPickupUsersByJobNoStoreParams,
  GetOnAirUsersParams,
} from '@/types/userProfile'
import {
  BalanceResponse,
  ListWithdrawalParams,
  ListWithdrawalsResponse,
  RegsiterWithdrawalParams,
  RegsiterWithdrawalResponse,
} from '@/types/withdrawal'

export interface RootState {
  signedIn: boolean
  email: string | null
  userNo: string | null
  ownUserInfo?: User
  notificationCount: number
  ngWords: string[]
}

// initial state
const state: RootState = {
  signedIn: isTokenExists(),
  email: null,
  userNo: null,
  notificationCount: 0,
  ngWords: [],
}

const getters = {
  /**
   * storeに保存されている自身のユーザ情報を取得
   * @param state
   */
  ownUserInfo(state: RootState): User | undefined {
    return state.ownUserInfo
  },
  /**
   * ログイン状態を取得
   * @param state
   */
  signedIn(state: RootState): boolean {
    return state.signedIn
  },
  /**
   * emailの取得
   * MailAuth.vueの37行目でリダイレクトするときに
   * パラメータが破損するのでstoreを使用
   * @param state
   * @returns
   */
  email(state: RootState): string | null {
    return state.email
  },
  userNo(state: RootState): string | null {
    return state.userNo
  },
  /**
   * 通知一覧件数を取得
   * @param state
   */
  notificationCount(state: RootState): number {
    return state.notificationCount
  },
  /**
   * NGワードを取得
   * @param state
   */
  ngWords(state: RootState): string[] {
    return state.ngWords
  },
}

const mutations = {
  /**
   * tokenをlocalStorageに保持
   * @param state
   * @param token
   */
  saveToken(state: RootState, token: string): void {
    setToken(token)
    setAuthHeader(`Bearer ${token}`)
    state.signedIn = true
  },
  /**
   * tokenをlocalStorageから削除
   * @param state
   */
  removeToken(state: RootState): void {
    removeToken()
    setAuthHeader('')
    state.signedIn = false
    state.notificationCount = 0
  },
  /**
   * 自身のユーザ情報をstoreに保持
   * @param state
   * @param userInfo
   */
  setOwnUserInfo(state: RootState, userInfo?: User): void {
    state.ownUserInfo = userInfo
  },
  /**
   * email情報をstoreに保持
   * @param state
   * @param email
   */
  saveEmail(state: RootState, email: string | null): void {
    state.email = email
  },
  saveUserNo(state: RootState, userNo: string | null): void {
    state.userNo = userNo
  },
  setNotificationCount(state: RootState, count: number) {
    state.notificationCount = count
  },
  /**
   * NGワードをstoreに保持
   * @param state
   * @param userInfo
   */
  setNgWords(state: RootState, ngWords: string[]): void {
    state.ngWords = ngWords
  },
}

const actions = {
  /**
   * ログイン処理
   * @param param0
   * @param token
   * @param userInfo
   */
  login({ commit }: { commit: Commit }, { token, userInfo }: { token: string; userInfo?: User }): void {
    commit('saveToken', token)
    commit('setOwnUserInfo', userInfo)
  },

  /**
   * メールアドレスでログイン
   * @param param0
   * @param param1
   * @returns
   */
  async mailLogin({ commit }: { commit: Commit }, params: UserMailLogin): Promise<void> {
    await apiServer
      .post<ISignInUserResponse>('/mail-login', params)
      .then((res) => {
        commit('saveToken', res.data.data.token)
        commit('setOwnUserInfo', res.data.data.user)
      })
      .catch((e) => Promise.reject(e))

    return Promise.resolve()
  },

  /**
   * ログアウト処理
   * @param param0
   */
  logout({ commit }: { commit: Commit }): void {
    commit('removeToken')
    commit('setOwnUserInfo')
  },

  /**
   * ユーザー登録認証メール送信 (認証無し)
   * @param params
   */
  async sendMailCreatingUser({ commit }: { commit: Commit }, params: UserAuthMail): Promise<void> {
    // 認証無し
    await apiServer.post(`/register/email`, params)
  },

  /**
   * ユーザー登録認証メール送信検証 (認証無し)
   * @param params
   */
  async authUser({ commit }: { commit: Commit }, params: UserAuth): Promise<void> {
    // TODO 動作確認
    await apiServer.post(`/register/auth`, params)
  },

  /**
   * パスワード変更認証メール送信
   * @param param0
   * @param params
   */
  async sendMailUpdatingPassword({ commit }: { commit: Commit }, params: PasswordAuthMail): Promise<void> {
    // 認証無し
    await apiServer.post(`/password/email`, params)
  },

  /**
   * パスワード変更認証コード検証
   * @param param0
   * @param params
   */
  async authPassword({ commit }: { commit: Commit }, params: PasswordAuth): Promise<void> {
    // 認証無し
    await apiServer.post(`/password/auth`, params)
  },

  /**
   * パスワード変更
   * @param param0
   * @param params
   */
  async updatePassword({ commit }: { commit: Commit }, params: PasswordUpdate): Promise<void> {
    await apiServer.patch(`/password`, params)
  },

  /**
   * mailAddress追加確認メール送信
   * @param param0
   * @param params
   */
  async sendAddMailAddressMail({ commit }: { commit: Commit }, params: SendAddMailAddressMailForm): Promise<void> {
    await apiServer.post(`/add/mail-address/email`, params)
  },

  /**
   * mailAddress追加検証
   * @param param0
   * @param params
   */
  async verifyAddMailAddress(
    { commit }: { commit: Commit },
    params: VerifyAddMailAddress
  ): Promise<VerifyAddMailAddressResponse> {
    const res = await apiServer.post(`/add/mail-address/auth`, params)

    return res.data.email
  },

  /**
   * mailAddress追加
   * @param param0
   * @param params
   */
  async addMailAddress({ commit }: { commit: Commit }, params: AddMailAddressForm): Promise<void> {
    await apiServer.post(`/add/mail-address`, params)
  },

  /**
   * 自身のユーザ情報を取得して、storeに保存
   * @param param0
   */
  async getUserDetail({ commit }: { commit: Commit }): Promise<UserResponse> {
    const url = '/user-detail'
    const res = await apiServer.get(url)

    commit('setOwnUserInfo', res.data.data.user)

    return res.data
  },

  /**
   * 指定userNoのユーザプロフィール取得
   * @param commit
   * @param userNo
   */
  async getUserProfile(
    { commit }: { commit: Commit },
    { userNo }: { userNo?: number }
  ): Promise<UserProfileDetailResponse> {
    const url = `/user-profile/${userNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /**
   * 表示用ユーザIDのユーザプロフィール取得
   * @param commit
   * @param indicateUserId
   */
  async getUserProfileByIndicateUserId(
    { commit }: { commit: Commit },
    { indicateUserId }: { indicateUserId: string }
  ): Promise<UserProfileDetailResponse> {
    const url = `/user-profile-by-id/${indicateUserId}`
    const res = await apiServer.get(url)

    return res.data
  },

  async getPickupUsers(): Promise<AxiosResponse> {
    const url = '/pickup-users'
    const res = await apiServer.get(url)

    return res
  },

  /**
   * ジョブマスター取得
   */
  async getJobMaster(): Promise<AxiosResponse> {
    const url = '/job-master'
    const res = await apiServer.get(url)

    return res
  },

  /**
   * ジョブサブマスター取得
   * @param param0
   * @param jobMasterNo
   */
  async getJobSubMaster(
    { commit }: { commit: Commit },
    { jobMasterNo }: { jobMasterNo: number }
  ): Promise<AxiosResponse> {
    const url = `/${jobMasterNo}/job-sub-master`
    const res = await apiServer.get(url)

    return res
  },

  /**
   * 地域マスター取得
   * @param param0
   * @param parentNo
   */
  async getAreaMaster({ commit }: { commit: Commit }, { parentNo }: { parentNo?: number }): Promise<AxiosResponse> {
    const url = '/area-master'
    const params = { parentNo }
    const res = await apiServer.get(url, { params })

    return res
  },

  /**
   * 地域マスター詳細取得
   * @param param0
   * @param parentNo
   */
  async getAreaMasterDetail(
    { commit }: { commit: Commit },
    { areaMasterNo }: { areaMasterNo: number }
  ): Promise<{ data: { areaMaster: AreaMaster } }> {
    const url = `/area-masters/${areaMasterNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /**
   * 特集記事一覧取得
   */
  async getListFeatureArticles(): Promise<AxiosResponse> {
    const url = '/feature-articles'
    const res = await apiServer.get(url)

    return res
  },

  /**
   * お知らせ取得
   * @param param0
   * @param limit
   */
  async getListNotices(
    { commit }: { commit: Commit },
    { limit, offset }: { limit?: number; offset?: number }
  ): Promise<Array<Notice>> {
    const url = '/notices'
    const params = {
      limit: limit,
      offset: offset,
    }
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * 発注した依頼一覧（予約）を取得
   * @param param0
   * @param param1
   */
  async getOrderedOrders(
    { commit }: { commit: Commit },
    { limit, offset, isAll, statuses }: { limit?: number; offset?: number; isAll?: boolean; statuses?: number[] }
  ): Promise<GetOrderedOrdersResponse> {
    const url = '/orders'
    const params = {
      limit,
      offset,
      type: ORDER_TYPES.ORDERED,
      isAll: isAll,
      statuses: statuses,
    }
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * 受注した依頼一覧（予約）を取得
   * @param param0
   * @param param1
   */
  async getReceivedOrders(
    { commit }: { commit: Commit },
    searchParams: GetReceivedOrdersParams
  ): Promise<GetReceivedOrdersResponse> {
    const url = '/orders'
    const params = {
      type: ORDER_TYPES.RECEIVED,
      ...searchParams,
    }
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * 依頼詳細を取得
   * @param param0
   * @param orderNo
   */
  async getOrderDetail({ commit }: { commit: Commit }, orderNo: number): Promise<GetOrderDetailResponse> {
    const url = `/orders/${orderNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /**
   * オファー条件取得
   */
  async getOfferTerms({ commit }: { commit: Commit }, { userNo }: { userNo: number }): Promise<OfferTermsList> {
    const url = `/user/offerTerms/${userNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /**
   * 依頼の新規登録
   * @param param0
   * @param params
   */
  async registerOrder({ commit }: { commit: Commit }, params: RegisterOrderParams): Promise<RegisterOrderResponse> {
    const url = '/order/register'
    const res = await apiServer.post(url, params)

    return res.data
  },

  /**
   * 依頼受注
   * @param param0
   * @param orderNo
   */
  async receiveOrder({ commit }: { commit: Commit }, orderNo: number): Promise<UpdateOrderResponse> {
    const url = `/orders/${orderNo}/receive`
    const res = await apiServer.patch(url)

    return res.data
  },

  /**
   * 依頼依頼数取得
   * @returns
   */
  async getOrderedOrdersCount({ commit }: { commit: Commit }) {
    const url = '/orders/receive/count'
    const res = await apiServer.get(url)

    return res.data.data.count
  },

  /**
   * 受注依頼数取得
   * @returns
   */
  async getReceivedOrdersCount({ commit }: { commit: Commit }) {
    const url = '/orders/order/count'
    const res = await apiServer.get(url)

    return res.data.data.count
  },

  /**
   * 未読受注依頼数取得
   * @returns
   */
  async getNotReadReceivedOrdersCount({ commit }: { commit: Commit }) {
    const url = '/orders/receive/not-read/count'
    const res = await apiServer.get(url)

    return res.data.data.count
  },

  /**
   * 完了した依頼一覧を取得
   * Order＋LiveOrder
   * @param param0
   */
  async getCompletedOrders(
    { commit }: { commit: Commit },
    params: GetCompletedOrdersParams
  ): Promise<GetCompletedOrdersResponse> {
    const url = '/orders/completed'
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * 受注者依頼集計を取得
   * @param param0
   * @param params
   */
  async getCompletedOrderSummary(
    { commit }: { commit: Commit },
    params: GetCompletedOrderSummaryParams
  ): Promise<CompletedOrderSummary> {
    const url = '/orders/completed/summary'
    const res = await apiServer.get(url, { params })

    return res.data.data
  },

  /**
   * 依頼のキャンセル
   * @param param0
   * @param orderNo
   * @returns
   */
  async cancelOrder({ commit }: { commit: Commit }, params: CancelOrderParams): Promise<CancelOrderResponse> {
    const url = '/order/cancel'
    const res = await apiServer.post<CancelOrderResponse>(url, params)

    return res.data
  },

  /**
   * 通報処理
   * @param param0
   * @param params
   * @returns
   */
  async userReport({ commit }: { commit: Commit }, params: UserReportRequest): Promise<UserReportResponse> {
    const url = '/order/user/report'
    const res = await apiServer.post<UserReportResponse>(url, params)

    return res.data
  },

  /**
   * 受注者売上明細データを取得
   * @param param0
   * @param targetDate
   */
  async getSalesDetails({ commit }: { commit: Commit }, targetDate: string): Promise<Blob> {
    const url = '/orders/completed/sales-details'
    const res = await apiServer.get<Blob>(url, { params: { targetDate }, responseType: 'blob' })

    return res.data
  },

  /**
   * GMOLinkplusのURLを生成
   * @param param0
   * @param gmoOrderId
   */
  async generateGmoLinkplusUrl(
    { commit }: { commit: Commit },
    params: GenerateGmoLinkplusUrlParams
  ): Promise<GenerateGmoLinkplusUrlResponse> {
    const url = '/gmo/url'
    const res = await apiServer.post<GenerateGmoLinkplusUrlResponse>(url, params)

    return res.data
  },

  /**
   * 通知一覧
   */
  async getNotifications(
    { commit }: { commit: Commit },
    { limit, offset }: { limit?: number; offset?: number }
  ): Promise<OfferTermsList> {
    const url = '/notifications'
    const params = {
      limit,
      offset,
    }
    const res = await apiServer.get<OfferTermsList>(url, { params })

    return res.data
  },

  /**
   * 通知既読
   */
  async markNotificationAsRead(
    { commit }: { commit: Commit },
    { notificationNo }: { notificationNo: number }
  ): Promise<void> {
    const url = `/notification/${notificationNo}/mark-as-read`
    await apiServer.patch<ICommonSuccessResponse>(url)
  },

  /**
   * 通知未読件数
   * @returns 未読件数
   */
  async getUnreadNotificationCount({ commit }: { commit: Commit }): Promise<number> {
    const url = '/notification/unread/count'
    const res = await apiServer.get<UnreadNotificationCount>(url)

    commit('setNotificationCount', res.data.data.count)

    return res.data.data.count
  },

  /**
   * 評価登録
   * @param param0
   * @param params
   */
  async registerReview({ commit }: { commit: Commit }, params: RegisterReviewParams): Promise<void> {
    const url = '/review'
    await apiServer.post<ICommonSuccessResponse>(url, params)
  },

  /**
   * 評価登録済み判定
   * @param param0
   * @param orderNo
   */
  async isRegisteredReview({ commit }: { commit: Commit }, orderNo: number): Promise<boolean> {
    const url = `/review/${orderNo}/isRegistered`
    const res = await apiServer.get<IsRegisteredReviewResponse>(url)

    return res.data.data.isRegisteredReview
  },

  /**
   * 残高取得
   * @returns 残高/GMO送金手数料関連情報
   */
  async getBalance(): Promise<BalanceResponse> {
    const url = '/balance'
    const res = await apiServer.get<BalanceResponse>(url)

    return res.data
  },

  /**
   * 出金依頼
   * @param param0
   * @param params 出金依頼内容
   * @returns 依頼結果
   */
  async registerWithdrawal(
    { commit }: { commit: Commit },
    params: RegsiterWithdrawalParams
  ): Promise<RegsiterWithdrawalResponse> {
    const url = '/withdrawal/register'
    const res = await apiServer.post<RegsiterWithdrawalResponse>(url, params)

    return res.data
  },

  /**
   * 出金履歴一覧
   * @param param0
   * @param params 一覧取得パラメータ
   * @returns 出金履歴内容
   */
  async getWithdrawals({ commit }: { commit: Commit }, params: ListWithdrawalParams): Promise<ListWithdrawalsResponse> {
    const url = '/withdrawals'
    const res = await apiServer.get<ListWithdrawalsResponse>(url, { params })

    return res.data
  },

  /**
   * 請求登録
   * @param param0
   * @param orderNo
   * @param params
   */
  async registerBilling(
    { commit }: { commit: Commit },
    params: { params: RegisterBillingParams }
  ): Promise<RegisterBillingResponse> {
    const url = '/billing/register'
    const res = await apiServer.post<RegisterBillingResponse>(url, params)

    return res.data
  },

  /**
   * チャットルーム存在確認
   * @param commit
   * @param receiverNo
   */
  async checkChatRoomIsCreated(
    { commit }: { commit: Commit },
    { receiverNo }: { receiverNo: number }
  ): Promise<IsCreatedRoomResponse> {
    const url = `/chat/room/isCreated/${receiverNo}`
    const res = await apiServer.get<IsCreatedRoomResponse>(url)

    return res.data
  },

  /**
   * チャットルーム取得
   * @param commit
   * @param roomNo
   * @param from
   */
  async getChatRoom(
    { commit }: { commit: Commit },
    { roomNo, from }: { roomNo: number; from: number }
  ): Promise<GetRoomResponse> {
    const url = `/chat/room/${roomNo}/${from}`
    const res = await apiServer.get<GetRoomResponse>(url)

    return res.data
  },

  /**
   * チャットルーム作成
   * @param commit
   * @param formData
   */
  async openChatRoom({ commit }: { commit: Commit }, { formData }: { formData: FormData }): Promise<OpenRoomResponse> {
    const url = `/chat/room`
    const res = await apiServerFormData.post<OpenRoomResponse>(url, formData)

    return res.data
  },

  /**
   * チャットルーム更新
   * @param commit
   * @param roomNo
   * @param formData
   */
  async updateChatRoom(
    { commit }: { commit: Commit },
    { roomNo, formData }: { roomNo: number; formData: FormData }
  ): Promise<UpdateRoomResponse> {
    const url = `/chat/room/${roomNo}`
    const res = await apiServerFormData.put<UpdateRoomResponse>(url, formData)

    return res.data
  },

  /**
   * チャットルーム閉鎖
   * @param commit
   * @param roomNo
   */
  async closeChatRoom({ commit }: { commit: Commit }, { roomNo }: { roomNo: number }): Promise<CloseRoomResponse> {
    const url = `/chat/room/${roomNo}`
    const res = await apiServer.delete<CloseRoomResponse>(url)

    return res.data
  },

  /**
   * メンバー参加
   * @param commit
   * @param memberNo
   */
  async joinChatMember(
    { commit }: { commit: Commit },
    { memberNo }: { memberNo: number }
  ): Promise<JoinMemberResponse> {
    const url = `/chat/member/${memberNo}`
    const res = await apiServerFormData.put<JoinMemberResponse>(url)

    return res.data
  },

  /**
   * メンバー退出
   * @param commit
   * @param memberNo
   */
  async leaveChatMember(
    { commit }: { commit: Commit },
    { memberNo }: { memberNo: number }
  ): Promise<LeaveMemberResponse> {
    const url = `/chat/member/${memberNo}`
    const res = await apiServer.delete<LeaveMemberResponse>(url)

    return res.data
  },

  /**
   * メッセージフェッチ
   * @param commit
   * @param roomNo
   * @param from
   */
  async fetchChatMessages(
    { commit }: { commit: Commit },
    { roomNo, from }: { roomNo: number; from: number | undefined }
  ): Promise<FetchMessagesResponse> {
    let url = `/chat/fetch/${roomNo}`

    if (from !== undefined) {
      url += `/${from}`
    }

    const res = await apiServer.get<FetchMessagesResponse>(url)

    return res.data
  },

  /**
   * メッセージ既読状態送信
   * @param commit
   * @param memberNo
   * @param formData
   */
  async sendChatMessageReadStatuses(
    { commit }: { commit: Commit },
    { memberNo, formData }: { memberNo: number; formData: FormData }
  ): Promise<SendMessageStatesResponse> {
    const url = `/chat/fetch/${memberNo}`
    const res = await apiServerFormData.put<SendMessageStatesResponse>(url, formData)

    return res.data
  },

  /**
   * メッセージ送信
   * @param commit
   * @param roomNo
   * @param memberNo
   * @param formData
   */
  async sendChatMessage(
    { commit }: { commit: Commit },
    { roomNo, memberNo, formData }: { roomNo: number; memberNo: number; formData: FormData }
  ): Promise<SendMessageResponse> {
    const url = `/chat/message/${roomNo}/${memberNo}`
    const res = await apiServerFormData.post<SendMessageResponse>(url, formData)

    return res.data
  },

  /**
   * メッセージ更新
   * @param commit
   * @param messageNo
   * @param formData
   */
  async updateChatMessage(
    { commit }: { commit: Commit },
    { messageNo, formData }: { messageNo: number; formData: FormData }
  ): Promise<UpdateMessageResponse> {
    const url = `/chat/message/${messageNo}`
    const res = await apiServerFormData.put<UpdateMessageResponse>(url, formData)

    return res.data
  },

  /**
   * メッセージ削除
   * @param commit
   * @param messageNo
   */
  async deleteChatMessage(
    { commit }: { commit: Commit },
    { messageNo }: { messageNo: number }
  ): Promise<DeleteMessageResponse> {
    const url = `/chat/message/${messageNo}`
    const res = await apiServer.delete<DeleteMessageResponse>(url)

    return res.data
  },

  /**
   * リアクション追加
   * @param commit
   * @param messageNo
   * @param memberNo
   * @param formNo
   */
  async addChatReaction(
    { commit }: { commit: Commit },
    { messageNo, memberNo, formNo, emoji }: { messageNo: number; memberNo: number; formNo: number; emoji: string }
  ): Promise<AddReactionResponse> {
    const url = `/chat/reaction/${messageNo}/${memberNo}/${formNo}/${emoji}/`
    const res = await apiServer.post<AddReactionResponse>(url)

    return res.data
  },

  /**
   * リアクション削除
   * @param commit
   * @param messageNo
   * @param memberNo
   * @param formNo
   */
  async removeChatReaction(
    { commit }: { commit: Commit },
    { messageNo, memberNo, formNo, emoji }: { messageNo: number; memberNo: number; formNo: number; emoji: string }
  ): Promise<RemoveReactionResponse> {
    const url = `/chat/reaction/${messageNo}/${memberNo}/${formNo}/${emoji}/`
    const res = await apiServer.delete<RemoveReactionResponse>(url)

    return res.data
  },

  /**
   * 指定ユーザの通報状態を取得
   * @param commit
   * @param userNo
   */
  async isUserReported(
    { commit }: { commit: Commit },
    { userNo }: { userNo: number }
  ): Promise<IsUserReportedResponse> {
    const url = `/users/${userNo}/is-reported`
    const res = await apiServer.get<IsUserReportedResponse>(url)

    return res.data
  },

  /**
   * 指定ユーザが受注ユーザかどうかを取得
   * @param commit
   * @param userNo
   */
  async isUserReceiver(
    { commit }: { commit: Commit },
    { userNo }: { userNo: number }
  ): Promise<IsUserReceiverResponse> {
    const url = `/users/${userNo}/is-receiver`
    const res = await apiServer.get<IsUserReceiverResponse>(url)

    return res.data
  },

  /**
   * 受注ユーザー登録
   * @param param0
   * @returns
   */
  async receiverRegister({ commit }: { commit: Commit }, params: ReceiveRegister) {
    const url = '/receiver-register'
    const res = await apiServerFormData.post(url, params)

    return res
  },

  /**
   * 登録種別変更
   * @param param0
   * @returns
   */
  async registerTypeUpdate({ commit }: { commit: Commit }) {
    const url = '/register-type'
    const res = await apiServer.patch(url)

    return res.data
  },

  /**
   * ユーザー編集
   * @param param0
   * @returns
   */
  async userDetailUpdate({ commit }: { commit: Commit }, params: UserProfileUpdateRequest) {
    const url = '/user-detail/update'
    const res = await apiServerFormData.post(url, params)

    return res.data
  },

  /**
   * 口座情報登録・更新
   * @param param0
   * @returns
   */
  async userBankRegister({ commit }: { commit: Commit }, params: userBankRegisterBody) {
    const url = '/user-bank-register'
    const res = await apiServer.post(url, params)

    return res
  },

  /**
   * 口座情報削除
   * @param param0
   * @returns
   */
  async userBankDelete({ commit }: { commit: Commit }) {
    const url = '/user-bank-register'
    const res = await apiServer.delete(url)

    return res
  },

  /**
   * 電話番号で認証コード発行
   * @param param0
   * @param params
   */
  async phoneNumberAuth(
    { commit }: { commit: Commit },
    params: phoneNumberAuthRequest
  ): Promise<PhoneNumberAuthResponse> {
    const res = await apiServer.post(`/phone-number/auth`, params)

    return res.data
  },

  /**
   * 電話番号認証コードを検証
   * @param param0
   * @param params
   */
  async phoneNumberVerifyAuth(
    { commit }: { commit: Commit },
    params: phoneNumberVerifyAuthRequest
  ): Promise<PhoneNumberVerifyAuthResponse> {
    const res = await apiServer.post(`/phone-number/verify-auth`, params)

    return res.data
  },

  /**
   * 新規ユーザーリスト取得(ジョブカテゴリ抽出)
   * @param commit
   * @param range
   * @param offset
   * @param limit
   * @param jobMasNo
   */
  async getNewlyByJobNo(
    { commit }: { commit: Commit },
    { range, offset, limit, jobMasNo }: GetNewlyByJobNoStoreParams
  ): Promise<AxiosResponse> {
    const genQuery = (name: string, value: number, undefValue = 0) =>
      value !== undefValue ? `${name}=${value}` : undefined
    const getQueries = (range: number, offset: number, limit: number, jobMasNo: number, undefValue = 0) => {
      return [
        genQuery('range', range, undefValue),
        genQuery('offset', offset, undefValue),
        genQuery('limit', limit, undefValue),
        genQuery('jobMasNo', jobMasNo, undefValue),
      ]
        .filter((query) => query !== undefined)
        .join('&')
    }
    const url = `/user/newly?${getQueries(range, offset, limit, jobMasNo)}`

    const res = await apiServer.get(url)

    return res
  },

  /**
   * ピックアップユーザーリスト取得(ジョブカテゴリ抽出)
   * @param commit
   * @param jobMasNo
   */
  async getPickupUsersByJobNo(
    { commit }: { commit: Commit },
    { jobMasNo }: GetPickupUsersByJobNoStoreParams
  ): Promise<AxiosResponse> {
    const params = jobMasNo !== 0 ? `?jobMasNo=${jobMasNo}` : ''
    const url = `/pickup-users-by-job${params}`

    const res = await apiServer.get(url)

    return res
  },

  /**
   * 退会処理
   * @param commit
   */
  async quitUser({ commit }: { commit: Commit }): Promise<QuitUserResponse> {
    const res = await apiServer.post(`/quit-user`)

    return res.data
  },

  /**
   * NGワードリスト取得
   * @param commit
   * @param target
   */
  async listNgWords({ commit }: { commit: Commit }, { target }: { target: number }): Promise<ListNgWordsResponseData> {
    const res = await apiServer.get(`/ng-words/${target}`)

    return res.data
  },

  /**
   * ライブストリーミング状態取得
   * @param commit
   * @param hostId
   */
  async hasLiveStreaming(
    { commit }: { commit: Commit },
    { hostId }: HasLiveStreamingParams
  ): Promise<HasLiveStreamingResponseData> {
    const res = (await apiServer.get(`/hosted/is-live-streaming/${hostId}`)) as HasLiveStreamingResponse

    return res.data
  },

  /* ライブ登録
   * @param param0
   * @returns
   */
  async liveRegister({ commit }: { commit: Commit }, params: LiveRegisterRequest) {
    const url = '/live-register'
    const res = await apiServerFormData.post(url, params)

    return res
  },

  /* 配信したライブ配信一覧を取得
   * @param param0
   * @param param1
   */
  async getHostedLiveStreams(
    { commit }: { commit: Commit },
    { offset, limit }: { offset?: number; limit?: number }
  ): Promise<GetLiveStreamsResponse> {
    const url = '/hosted/live-streams'
    const params = { offset, limit }
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * ライブストリーム情報取得
   * @param commit
   */
  async getAgoraInfo({ commit }: { commit: Commit }): Promise<GetAgoraInfoResponseData> {
    const res = (await apiServer.get(`/live-stream/live/info`)) as GetAgoraInfoResponse

    return res.data
  },

  /**
   * アクセストークン取得
   * @param commit
   * @param uid
   * @param channel
   */
  async getAgoraAccessTokens(
    { commit }: { commit: Commit },
    { uid, channel }: GetAgoraAccessTokensParams
  ): Promise<GetAgoraAccessTokensResponseData> {
    const res = (await apiServer.get(`/live-stream/live/tokens/${uid}/${channel}`)) as GetAgoraAccessTokensResponse

    return res.data
  },

  /**
   * 配信開始
   * @param commit
   * @param hostNo
   * @param title
   * @param channel
   */
  async joinLiveStreamByHost(
    { commit }: { commit: Commit },
    { hostNo, title, channel }: JoinLiveStreamByHostRequest
  ): Promise<JoinLiveStreamByHostResponseData> {
    const res = (await apiServerFormData.post(`/hosted/live-stream`, {
      hostNo: hostNo,
      title: title,
      channel: channel,
    })) as JoinLiveStreamByHostResponse

    return res.data
  },

  /**
   * 配信終了
   * @param commit
   * @param liveStreamNo
   */
  async leaveLiveStreamByHost({ commit }: { commit: Commit }, { liveStreamNo }: LeaveLiveStreamByHostRequest) {
    const res = await apiServerFormData.put(`/hosted/live-stream`, { liveStreamNo: liveStreamNo })

    return res.data
  },

  /**
   * 配信参加
   * @param liveStreamNo
   * @param audienceNo
   */
  async joinLiveStreamByAudience(
    { commit }: { commit: Commit },
    { liveStreamNo, audienceNo }: JoinLiveStreamByAudienceRequest
  ): Promise<JoinLiveStreamByAudienceResponseData> {
    const res = (await apiServerFormData.post(`/viewed/live-stream`, {
      liveStreamNo: liveStreamNo,
      audienceNo: audienceNo,
    })) as JoinLiveStreamByAudienceResponse

    return res.data
  },

  /**
   * 配信退出
   * @param commit
   * @param liveStreamNo
   * @param audienceNo
   */
  async leaveLiveStreamByAudience(
    { commit }: { commit: Commit },
    { liveStreamNo, audienceNo }: LeaveLiveStreamByAudienceRequest
  ) {
    const res = await apiServerFormData.put(`/viewed/live-stream`, {
      liveStreamNo: liveStreamNo,
      audienceNo: audienceNo,
    })

    return res.data
  },

  /**
   * 配信ブロック
   * @param commit
   * @param hostNo
   * @param audienceNo
   */
  async blockAudience({ commit }: { commit: Commit }, { hostNo, audienceNo }: BlockAudienceRequest) {
    const res = await apiServerFormData.post(`/hosted/live-stream/block`, {
      hostNo: hostNo,
      audienceNo: audienceNo,
    })

    return res.data
  },

  /**
   * 配信ブロック解除
   * @param commit
   * @param hostNo
   * @param audienceNo
   */
  async releaseBlockedAudience({ commit }: { commit: Commit }, { hostNo, audienceNo }: ReleaseBlockedAudienceRequest) {
    const res = await apiServerFormData.put(`/hosted/live-stream/block`, {
      hostNo: hostNo,
      audienceNo: audienceNo,
    })

    return res.data
  },

  /**
   * 配信ミュート
   * @param commit
   * @param liveStreamNo
   * @param audienceNo
   */
  async muteAudience({ commit }: { commit: Commit }, { liveStreamNo, audienceNo }: MuteAudienceRequest) {
    const res = await apiServerFormData.post(`/hosted/live-stream/mute`, {
      liveStreamNo: liveStreamNo,
      audienceNo: audienceNo,
    })

    return res.data
  },

  /**
   * 配信ミュート解除
   * @param commit
   * @param liveStreamNo
   * @param audienceNo
   */
  async releaseMutedAudience(
    { commit }: { commit: Commit },
    { liveStreamNo, audienceNo }: ReleaseMutedAudienceRequest
  ) {
    const res = await apiServerFormData.put(`/hosted/live-stream/mute`, {
      liveStreamNo: liveStreamNo,
      audienceNo: audienceNo,
    })

    return res.data
  },

  /**
   * 配信ブロック状態取得
   * @param commit
   * @param hostId
   * @param audienceId
   */
  async getBlockMap(
    { commit }: { commit: Commit },
    { hostId, audienceId }: GetBlockMapRequest
  ): Promise<GetBlockMapResponseData> {
    const res = (await apiServer.get(`/viewed/live-stream/block/${hostId}/${audienceId}`)) as GetBlockMapResponse

    return res.data
  },

  /**
   * 配信ミュート状態取得
   * @param commit
   * @param liveStreamNo
   * @param audienceId
   */
  async getMuteMap(
    { commit }: { commit: Commit },
    { liveStreamNo, audienceId }: GetMuteMapRequest
  ): Promise<GetMuteMapResponseData> {
    const res = (await apiServer.get(`/viewed/live-stream/mute/${liveStreamNo}/${audienceId}`)) as GetMuteMapResponse

    return res.data
  },

  /**
   * 配信したライブ配信詳細を取得
   * @param param0
   * @param param1
   */
  async getHostedLiveStreamDetail(
    { commit }: { commit: Commit },
    { liveStreamNo }: { liveStreamNo: number }
  ): Promise<GetLiveStreamDetailResponse> {
    const url = `/hosted/live-streams/${liveStreamNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /**
   * 配信集計取得
   * @param param0
   * @param params
   */
  async getHostedLiveStreamSummary(
    { commit }: { commit: Commit },
    params: { startDate: string; endDate: string }
  ): Promise<HostedLiveStreamSummary> {
    const url = '/hosted/live-streams/summary'

    const res = await apiServer.get(url, { params })

    return res.data.data
  },

  /* 視聴したライブ配信一覧を取得
   * @param param0
   * @param param1
   */
  async getViewedLiveStreams(
    { commit }: { commit: Commit },
    { offset, limit }: { offset?: number; limit?: number }
  ): Promise<GetLiveStreamsResponse> {
    const url = '/viewed/live-streams'
    const params = { offset, limit }
    const res = await apiServer.get(url, { params })

    return res.data
  },

  /**
   * 視聴したライブ配信詳細を取得
   * @param param0
   * @param param1
   */
  async getViewedLiveStreamDetail(
    { commit }: { commit: Commit },
    { liveStreamNo }: { liveStreamNo: number }
  ): Promise<GetLiveStreamDetailResponse> {
    const url = `/viewed/live-streams/${liveStreamNo}`
    const res = await apiServer.get(url)

    return res.data
  },

  /* ライブ中ユーザ一覧取得(HOMEタブ用)
   * @param commit
   */
  async getOnAirUsers(
    { commit }: { commit: Commit },
    { range, offset, limit }: GetOnAirUsersParams
  ): Promise<AxiosResponse> {
    const genQuery = (name: string, value: number, undefValue = 0) =>
      value !== undefValue ? `${name}=${value}` : undefined
    const getQueries = (range: number, offset: number, limit: number, undefValue = 0) => {
      return [
        genQuery('range', range, undefValue),
        genQuery('offset', offset, undefValue),
        genQuery('limit', limit, undefValue),
      ]
        .filter((query) => query !== undefined)
        .join('&')
    }
    const url = `/on-air/new-users?${getQueries(range, offset, limit)}`

    const res = await apiServer.get(url)

    return res
  },

  /**
   * ライブ中ピックアップ一覧取得(HOMEタブ用)
   * @param commit
   */
  async getOnAirPickupUsers({ commit }: { commit: Commit }) {
    const url = `/on-air/pickup-users`
    const res = await apiServer.get(url)

    return res
  },

  /**
   * 視聴者一覧取得
   * @param commit
   * @param liveStreamNo
   * @param keyword
   * @param limit
   * @param offset
   */
  async getAudiences(
    { commit }: { commit: Commit },
    { liveStreamNo, keyword, limit, offset }: GetAudiencesRequest
  ): Promise<GetAudiencesResponseData> {
    const encodedKeyword = encodeURIComponent(JSON.stringify(keyword))
    const res = (await apiServer.get(
      `/hosted/live-stream/audiences/${liveStreamNo}?keyword=${encodedKeyword}&limit=${limit}&offset=${offset}`
    )) as GetAudiencesResponse

    return res.data
  },

  /**
   * 商品一覧取得
   * @param commit
   * @param hostId
   */
  async getProducts({ commit }: { commit: Commit }, { hostId }: GetProductsRequest): Promise<GetProductsResponseData> {
    const res = (await apiServer.get(`/live-stream/products/${hostId}`)) as GetProductsResponse

    return res.data
  },

  /**
   * 商品購入数カウントアップ
   * @param commit
   * @param productNo
   */
  async upProductCount(
    { commit }: { commit: Commit },
    { productNo }: UpProductCountRequest
  ): Promise<UpProductCountResponseData> {
    const res = (await apiServerFormData.put(`/viewed/live-stream/products/count-up`, {
      productNo: productNo,
    })) as UpProductCountResponse

    return res.data
  },

  /**
   * 商品購入数カウントダウン
   * @param commit
   * @param productNo
   */
  async downProductCount(
    { commit }: { commit: Commit },
    { productNo }: DownProductCountRequest
  ): Promise<DownProductCountResponseData> {
    const res = (await apiServerFormData.put(`/viewed/live-stream/products/count-down`, {
      productNo: productNo,
    })) as DownProductCountResponse

    return res.data
  },

  /**
   * 配信用GMOリンクURL生成
   * @param commit
   * @param productNo
   */
  async generateGmoLinkplusUrlForLiveStream(
    { commit }: { commit: Commit },
    { liveStreamNo, audienceNo, hostId, productNo, amount }: GenerateGmoLinkplusUrlForLiveStreamRequest
  ): Promise<GenerateGmoLinkplusUrlForLiveStreamResponseData> {
    const res = (await apiServerFormData.post(`/gmo/url-livestream`, {
      liveStreamNo: liveStreamNo,
      audienceNo: audienceNo,
      hostId: hostId,
      productNo: productNo,
      amount: amount,
    })) as GenerateGmoLinkplusUrlForLiveStreamResponse

    return res.data
  },

  /**
   * 商品注文取得
   * @param commit
   * @param liveOrderNo
   */
  async getLiveOrder(
    { commit }: { commit: Commit },
    { liveOrderNo }: GetLiveOrderRequest
  ): Promise<GetLiveOrderResponseData> {
    const res = (await apiServer.get(`/viewed/live-stream/order/${liveOrderNo}`)) as GetLiveOrderResponse

    return res.data
  },
}

export const key: InjectionKey<Store<RootState>> = Symbol()

export const module = {
  state,
  getters,
  mutations,
  actions,
}
const store = createStore<RootState>({
  ...module,
})
export default store
